import React from "react";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";

// Placeholder book data — swap slug for actual cover image in /images/books
// and update Amazon URLs when confirmed.
const BOOKS = [
  {
    title: "Operational Risk Management in Financial Services",
    subtitle: "A practical guide to establishing effective solutions",
    edition: "Second edition",
    publisher: "Kogan Page",
    year: "2024",
    isbn: "9781398617148",
    slug: "op-risk-2nd",
    ext: "png",
    url: "https://www.amazon.co.uk/dp/1398617148",
  },
  {
    title: "Operational Risk Management in Financial Services",
    subtitle: "A practical guide to establishing effective solutions",
    edition: "First edition",
    publisher: "Kogan Page",
    year: "2021",
    isbn: "9781789667080",
    slug: "op-risk-1st",
    ext: "png",
    url: "https://www.amazon.co.uk/dp/1789667089",
  },
];

export const Books = () => {
  return (
    <section
      id="books"
      data-testid="books-section"
      className="relative pt-24 md:pt-32 pb-12 md:pb-16 bg-white border-t border-[#E5E5E5] overflow-hidden"
    >

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 md:mb-20 items-end">
          <div className="lg:col-span-7">
            <p className="font-sans-body text-xs tracking-widest-2 uppercase text-[#3E7373] mb-6 flex items-center gap-3">
              <span className="inline-block w-10 h-px bg-[#3E7373]" />
              Books
            </p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-[64px] leading-[1.02] tracking-tight text-[#0D0D0D] font-medium">
              The written
              <br />
              <span className="italic text-[#3E7373]">record.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 font-sans-body text-[#4A4A4A] text-base md:text-lg leading-relaxed">
          Two editions of the practitioner's handbook - used today in classrooms and around board tables worldwide.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
          {BOOKS.map((book, idx) => (
            <motion.article
              key={book.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, delay: idx * 0.12, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`book-${idx}`}
              className="group grid grid-cols-1 sm:grid-cols-5 gap-8 items-start"
            >
              {/* Book cover */}
              <div className="sm:col-span-2">
                <div className="relative aspect-[2/3] w-full max-w-[240px] bg-transparent">
                  <img
                    src={`/images/books/${book.slug}.${book.ext || "jpg"}`}
                    alt={`${book.title} — ${book.edition}`}
                    loading="lazy"
                    decoding="async"
                    width="240"
                    height="360"
                    sizes="(max-width: 768px) 100vw, 240px"
                    className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_24px_36px_rgba(13,13,13,0.25)] group-hover:drop-shadow-[0_32px_50px_rgba(13,13,13,0.35)] group-hover:-translate-y-1 transition-all duration-700"
                  />
                </div>
              </div>

              {/* Info */}
              <div className="sm:col-span-3">
                <p className="font-sans-body text-[10px] tracking-widest-2 uppercase text-[#3E7373] mb-3">
                  {book.edition} · {book.publisher} · {book.year}
                </p>
                <h3 className="font-serif-display text-2xl md:text-[28px] text-[#0D0D0D] leading-[1.15] mb-3">
                  {book.title}
                </h3>
                <p className="font-serif-display italic text-[#3E7373] text-lg mb-6 leading-snug">
                  {book.subtitle}
                </p>
                <a
                  href={book.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid={`book-purchase-${idx}`}
                  className="inline-flex items-center gap-2 border-b border-[#0D0D0D] pb-1 font-sans-body text-[11px] tracking-widest-2 uppercase text-[#0D0D0D] hover:text-[#3E7373] hover:border-[#3E7373] transition-colors duration-300"
                >
                  Purchase on Amazon
                  <ExternalLink size={13} strokeWidth={1.4} />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Books;
